/**
 * Fingers on the felt, as the lamp's light.
 *
 * Put a finger on the empty felt and a soft pool of lamplight gathers under it;
 * draw it across the cloth and the light follows with a short, fading tail,
 * the way a lit patch of wool warms and cools. Every finger has its own —
 * two fingers are two pools — and the others at the table see yours, where it
 * is on *their* table (table coordinates, tableFrame.ts), in brass: the light
 * of someone else's hand, told apart from your own warmer white at a glance.
 *
 * It is only ever the felt. A touch that starts on a card, on a card in the
 * trick or on a control is that card's or that control's, exactly as before —
 * the surface that listens for fingers sits under the trick and stops short of
 * the hand band, where the lifted cards rise into the felt's box.
 *
 * Drawn on one canvas under the cards, and only while there is light to draw.
 * Positions arrive from the others ~14 times a second and are eased towards
 * every frame; a finger nobody has said anything about for a moment has been
 * lifted (the "all lifted" message is volatile, and can be lost).
 */
import { useEffect, useRef } from 'react'
import type { FeltTouchRelay } from '@cards/shared'
import { prefersReducedMotion, useSettings } from '../settings/settings'
import { shareFelt, subscribeFelt } from './gestures'
import { fromTable, measureFrame, toTable } from './tableFrame'
import type { Position, TableView } from './view'

/** How long a trail stays behind the finger. */
const TAIL_MS = 1100
/** How long the pool takes to go out once the finger lifts. */
const FADE_MS = 700
/** A remote finger unheard of for this long has been lifted. */
const STALE_MS = 650
/** Per-frame easing of a remote finger towards where it was last reported. */
const FOLLOW = 0.35
/** Radius of the pool of light under a fingertip, CSS px. */
const POOL = 28
/** The trail's glow: its radius at the tail and how much wider it is at the
 *  fingertip, CSS px, and how bright it is along its middle. */
const MIST_TAIL = 8
const MIST_HEAD = 14
const MIST_PEAK = 0.075
/** Distance between the soft dots the trail is laid down as, CSS px — close
 *  enough against their radius that they run together into one band. */
const MIST_STEP = 2
/** How much brighter the trail is laid down off screen than it is shown. The
 *  trail is faint, and stacked dots that faint would each round off to a
 *  level or two of an 8-bit canvas, leaving bands and losing the soft edge;
 *  built up brighter and turned down once, it keeps its whole falloff. */
const MIST_GAIN = 12

interface Point {
  x: number
  y: number
  t: number
}

interface Finger {
  /** Felt-local CSS px. */
  x: number
  y: number
  /** Where a remote finger is heading (reported), felt-local px. */
  tx: number
  ty: number
  trail: Point[]
  /** When it lifted, or null while it is down. */
  up: number | null
  /** When it was last heard of (remote). */
  heard: number
  mine: boolean
}

const WARM = '255, 240, 205'
const BRASS = '226, 185, 98'

export function FeltLight({ view }: { view: TableView }): JSX.Element | null {
  const [settings] = useSettings()
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const surface = useRef<HTMLDivElement | null>(null)
  const viewRef = useRef(view)
  viewRef.current = view
  const on = settings.feltTouch && !prefersReducedMotion()
  const see = settings.seeMoves

  useEffect(() => {
    const cv = canvas.current
    const felt = cv?.parentElement
    if (!cv || !felt || !on) return
    const ctx = cv.getContext('2d')
    if (!ctx) return
    const mist = document.createElement('canvas')
    const mctx = mist.getContext('2d')
    if (!mctx) return
    const dots = { [WARM]: dot(WARM), [BRASS]: dot(BRASS) }
    const fingers = new Map<string, Finger>()
    /** pointerId → the small id a finger travels under. */
    const mine = new Map<number, number>()
    let frame = 0

    const box = () => felt.getBoundingClientRect()
    const size = () => {
      const r = box()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.round(r.width * dpr)
      const h = Math.round(r.height * dpr)
      if (cv.width !== w || cv.height !== h) {
        cv.width = w
        cv.height = h
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      return r
    }

    const kick = () => {
      if (!frame) frame = requestAnimationFrame(draw)
    }

    const draw = () => {
      frame = 0
      const r = size()
      const now = performance.now()
      ctx.clearRect(0, 0, r.width, r.height)
      ctx.globalCompositeOperation = 'lighter'
      // The trails go down on their own canvas first, one CSS px to its pixel:
      // a glow this soft loses nothing at that size.
      const mw = Math.max(1, Math.round(r.width))
      const mh = Math.max(1, Math.round(r.height))
      if (mist.width !== mw || mist.height !== mh) {
        mist.width = mw
        mist.height = mh
      } else mctx.clearRect(0, 0, mw, mh)
      mctx.globalCompositeOperation = 'lighter'
      let alive = false
      for (const [key, f] of fingers) {
        if (!f.mine && f.up === null && now - f.heard > STALE_MS) f.up = now
        if (!f.mine && f.up === null) {
          f.x += (f.tx - f.x) * FOLLOW
          f.y += (f.ty - f.y) * FOLLOW
          extend(f, now)
        }
        while (f.trail.length > 0 && now - f.trail[0].t > TAIL_MS) f.trail.shift()
        const out = f.up === null ? 1 : Math.max(0, 1 - (now - f.up) / FADE_MS)
        if (out <= 0 && f.trail.length === 0) {
          fingers.delete(key)
          continue
        }
        alive = true
        trail(mctx, dots[f.mine ? WARM : BRASS], f, now)
        pool(ctx, f, out)
      }
      if (alive) {
        ctx.globalAlpha = 1 / MIST_GAIN
        ctx.drawImage(mist, 0, 0, r.width, r.height)
        ctx.globalAlpha = 1
      }
      ctx.globalCompositeOperation = 'source-over'
      if (alive) kick()
    }

    /** Your fingers, in your own table coordinates, to the others. */
    const share = () => {
      const f = measureFrame()
      if (!f) return
      const r = box()
      const list: { id: number; x: number; y: number }[] = []
      for (const [pid, id] of mine) {
        const finger = fingers.get(`me:${pid}`)
        if (!finger || finger.up !== null) continue
        const at = toTable(r.left + finger.x, r.top + finger.y, f, f.sy * 2)
        list.push({ id, x: at.x, y: at.y })
      }
      shareFelt({ fingers: list })
    }

    const el = surface.current
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (mine.size >= 5) return
      try {
        el?.setPointerCapture(e.pointerId)
      } catch {
        /* gone already */
      }
      const used = new Set(mine.values())
      let id = 0
      while (used.has(id)) id++
      mine.set(e.pointerId, id)
      const r = box()
      const x = e.clientX - r.left
      const y = e.clientY - r.top
      fingers.set(`me:${e.pointerId}`, { x, y, tx: x, ty: y, trail: [{ x, y, t: performance.now() }], up: null, heard: 0, mine: true })
      share()
      kick()
    }
    const move = (e: PointerEvent) => {
      const f = fingers.get(`me:${e.pointerId}`)
      if (!f || f.up !== null) return
      const r = box()
      const now = performance.now()
      // Every sample the browser folded into this event, not just the last:
      // a fast stroke reported once a frame is a polyline with corners.
      const samples = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : []
      for (const s of samples.length > 0 ? samples : [e]) {
        f.x = s.clientX - r.left
        f.y = s.clientY - r.top
        extend(f, now)
      }
      share()
      kick()
    }
    const up = (e: PointerEvent) => {
      const f = fingers.get(`me:${e.pointerId}`)
      if (!f) return
      f.up = performance.now()
      mine.delete(e.pointerId)
      share()
      kick()
    }
    el?.addEventListener('pointerdown', down)
    el?.addEventListener('pointermove', move)
    el?.addEventListener('pointerup', up)
    el?.addEventListener('pointercancel', up)

    /** The others' fingers, on this table. */
    const off = subscribeFelt((t: FeltTouchRelay) => {
      if (!see) return
      const v = viewRef.current
      if (t.seat === v.mySeat) return
      const position = v.seats[t.seat]?.position
      const f = measureFrame()
      if (!position || !f) return
      const r = box()
      const now = performance.now()
      const live = new Set<string>()
      for (const finger of t.fingers) {
        const key = `${t.seat}:${finger.id}`
        live.add(key)
        const span = position === 's' || position === 'n' ? f.sy : f.sx
        const [px, py] = fromTable(finger.x, finger.y, position as Position, f, span * 2)
        const x = px - r.left
        const y = py - r.top
        const known = fingers.get(key)
        if (!known || known.up !== null) {
          fingers.set(key, { x, y, tx: x, ty: y, trail: [{ x, y, t: now }], up: null, heard: now, mine: false })
        } else {
          known.tx = x
          known.ty = y
          known.heard = now
        }
      }
      // A finger this seat no longer lists has lifted.
      for (const [key, finger] of fingers) {
        if (key.startsWith(`${t.seat}:`) && !live.has(key) && finger.up === null) finger.up = now
      }
      kick()
    })

    return () => {
      off()
      el?.removeEventListener('pointerdown', down)
      el?.removeEventListener('pointermove', move)
      el?.removeEventListener('pointerup', up)
      el?.removeEventListener('pointercancel', up)
      if (mine.size > 0) shareFelt({ fingers: [] })
      if (frame) cancelAnimationFrame(frame)
      ctx.clearRect(0, 0, cv.width, cv.height)
    }
  }, [on, see])

  if (!on) return null
  return (
    <>
      <canvas className="felt-light" ref={canvas} aria-hidden="true" />
      <div className="felt-surface" ref={surface} aria-hidden="true" />
    </>
  )
}

/** Add the finger's place to its trail — once it has moved. A finger held
 *  still adds nothing: every point is drawn additively, and a still finger
 *  adding one a frame piled its light up to full white where it rested. */
function extend(f: Finger, now: number): void {
  const last = f.trail[f.trail.length - 1]
  if (last && Math.hypot(f.x - last.x, f.y - last.y) < 2) return
  f.trail.push({ x: f.x, y: f.y, t: now })
}

/** A soft round dot of light in the given tint, drawn once and stamped along
 *  every trail: bright at its centre and falling away to nothing, with no edge. */
function dot(tint: string): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = c.height = 64
  const g = c.getContext('2d')
  if (!g) return c
  const fall = g.createRadialGradient(32, 32, 0, 32, 32, 32)
  for (let i = 0; i <= 8; i++) {
    const x = i / 8
    fall.addColorStop(x, `rgba(${tint}, ${(Math.exp(-4 * x * x) * (1 - x)).toFixed(3)})`)
  }
  g.fillStyle = fall
  g.fillRect(0, 0, 64, 64)
  return c
}

/** One finger's trail: a soft glow that narrows and fades behind it, laid
 *  down (brighter than shown, see MIST_GAIN) on the trail canvas.
 *
 *  It follows a curve through the trail's points rather than straight runs
 *  between them: each piece runs from the midpoint of one step to the midpoint
 *  of the next, bending through the point between, so neighbouring pieces meet
 *  on the same heading. Along that curve it is a row of soft dots an even
 *  distance apart, which run together into a band with no edge and no joints —
 *  a stroked line has both, and drawn additively every joint shows. */
function trail(ctx: CanvasRenderingContext2D, light: HTMLCanvasElement, f: Finger, now: number): void {
  const t = f.trail
  const n = t.length
  if (n < 2) return
  const strength = f.mine ? 1 : 0.85
  // How far along the current run the next dot falls.
  let ahead = 0
  const stamp = (x: number, y: number, at: number) => {
    const k = Math.max(0, 1 - (now - at) / TAIL_MS)
    // Held most of its life, then let go: smoothstep rather than a square, so
    // the trail stays readable for a while behind the finger.
    const fade = k * k * (3 - 2 * k) * strength
    if (fade < 0.003) return
    const r = MIST_TAIL + MIST_HEAD * k
    // Dots MIST_STEP apart, each this bright at its centre, add up to
    // MIST_PEAK along the middle of the band.
    ctx.globalAlpha = Math.min(1, ((MIST_PEAK * MIST_GAIN * MIST_STEP) / (0.8 * r)) * fade)
    ctx.drawImage(light, x - r, y - r, 2 * r, 2 * r)
  }
  const run = (p: Point, q: Point) => {
    const len = Math.hypot(q.x - p.x, q.y - p.y)
    let d = ahead
    for (; d <= len; d += MIST_STEP) {
      const u = len > 0 ? d / len : 0
      stamp(p.x + (q.x - p.x) * u, p.y + (q.y - p.y) * u, p.t + (q.t - p.t) * u)
    }
    ahead = d - len
  }
  const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, t: (a.t + b.t) / 2 })
  for (let i = 1; i < n; i++) {
    const a = i === 1 ? t[0] : mid(t[i - 2], t[i - 1])
    const b = mid(t[i - 1], t[i])
    const c = t[i - 1]
    // The curve, walked as a few short straight runs.
    let p = a
    for (let j = 1; j <= 4; j++) {
      const u = j / 4
      const v = 1 - u
      const q =
        i === 1
          ? { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, t: a.t + (b.t - a.t) * u }
          : { x: v * v * a.x + 2 * v * u * c.x + u * u * b.x, y: v * v * a.y + 2 * v * u * c.y + u * u * b.y, t: a.t + (b.t - a.t) * u }
      run(p, q)
      p = q
    }
    // The newest piece carries on to the fingertip itself.
    if (i === n - 1) run(b, t[i])
  }
  ctx.globalAlpha = 1
}

/** The pool of light under a fingertip, lit from its centre. */
function pool(ctx: CanvasRenderingContext2D, f: Finger, out: number): void {
  const tint = f.mine ? WARM : BRASS
  const strength = f.mine ? 1 : 0.85
  if (out <= 0) return
  const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, POOL)
  // Kept faint on purpose: a warmth in the cloth under the finger, not a
  // light shone on it. It should be something you notice, not look at.
  g.addColorStop(0, `rgba(${tint}, ${(0.11 * out * strength).toFixed(3)})`)
  g.addColorStop(0.45, `rgba(${tint}, ${(0.04 * out * strength).toFixed(3)})`)
  g.addColorStop(1, `rgba(${tint}, 0)`)
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(f.x, f.y, POOL, 0, Math.PI * 2)
  ctx.fill()
}

